import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiChevronDown } from "react-icons/fi";
import siteConfig from "../config/siteConfig";
import "../css/footer.scss";

const DESKTOP_FOOTER_QUERY = "(min-width: 681px)";

const getInitialLinkGroups = () => {
  const desktop =
    typeof window === "undefined" ||
    !window.matchMedia ||
    window.matchMedia(DESKTOP_FOOTER_QUERY).matches;
  return { help: desktop, about: desktop };
};

function Footer() {
  const [openLinkGroups, setOpenLinkGroups] = useState(getInitialLinkGroups);
  const brandLabel =
    siteConfig.logo?.text || siteConfig.logoText || siteConfig.brandName;
  const evergreenLabel = siteConfig.collections.coreLabel;
  const currentYear = new Date().getFullYear();
  const contactAddress =
    siteConfig.contact?.address || siteConfig.contact?.location;
  const copyright = String(
    siteConfig.copyright || `${siteConfig.brandName}. All rights reserved.`
  )
    .replace(/©/g, "")
    .trim();

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const helpLinks = [
    { label: "Contact", to: "/contact" },
    { label: "Shipping & delivery", to: "/shipping" },
    { label: "Returns", to: "/returns" },
    { label: "FAQs", to: "/faqs" },
    { label: "Size guide", to: "/size-guide" },
    { label: "Payment & security", to: "/payment-security" },
  ];

  const aboutLinks = [
    { label: "Our story", to: "/about" },
    { label: "New arrivals", to: "/shop?collection=new" },
    { label: "Shop all", to: "/shop" },
    { label: evergreenLabel, to: "/shop?collection=365" },
    { label: "Materials & care", to: "/materials-care" },
  ];

  const legalLinks = [
    { label: "Privacy", to: "/privacy" },
    { label: "Cookies", to: "/cookies" },
    { label: "FAQs", to: "/faqs" },
    { label: "Terms", to: "/terms" },
    { label: "Shipping", to: "/shipping" },
    { label: "Returns", to: "/returns" },
    { label: "Accessibility", to: "/accessibility" },
  ];

  useEffect(() => {
    const mediaQuery = window.matchMedia?.(DESKTOP_FOOTER_QUERY);
    if (!mediaQuery) return undefined;

    const syncLinkGroups = () => {
      setOpenLinkGroups({
        help: mediaQuery.matches,
        about: mediaQuery.matches,
      });
    };
    syncLinkGroups();
    mediaQuery.addEventListener?.("change", syncLinkGroups);
    return () => mediaQuery.removeEventListener?.("change", syncLinkGroups);
  }, []);

  const handleLinkGroupToggle = (group) => (event) => {
    const desktop = window.matchMedia?.(DESKTOP_FOOTER_QUERY)?.matches;
    setOpenLinkGroups((current) => ({
      ...current,
      [group]: desktop ? true : event.currentTarget.open,
    }));
  };

  return (
    <footer className="site-footer" role="contentinfo">
      <div className="site-footer__inner">
        <div className="footer-navigation">
          <details
            className="footer-link-group"
            open={openLinkGroups.help}
            onToggle={handleLinkGroupToggle("help")}
          >
            <summary id="footer-help-title">
              Help &amp; Contact <FiChevronDown aria-hidden="true" />
            </summary>
            <nav
              className="footer-link-group__content"
              aria-labelledby="footer-help-title"
            >
              {helpLinks.map((item) => (
                <Link key={item.label} to={item.to} onClick={scrollToTop}>
                  {item.label}
                </Link>
              ))}
            </nav>
          </details>

          <details
            className="footer-link-group"
            open={openLinkGroups.about}
            onToggle={handleLinkGroupToggle("about")}
          >
            <summary id="footer-about-title">
              About <FiChevronDown aria-hidden="true" />
            </summary>
            <nav
              className="footer-link-group__content"
              aria-labelledby="footer-about-title"
            >
              {aboutLinks.map((item) => (
                <Link key={item.label} to={item.to} onClick={scrollToTop}>
                  {item.label}
                </Link>
              ))}
            </nav>
          </details>
        </div>

        <section
          className="footer-social"
          aria-labelledby="footer-service-title"
        >
          <h2 id="footer-service-title">Customer care</h2>
          <p className="footer-social__hours">{siteConfig.contact.hours}</p>
          <div className="footer-social__links">
            <a href={`mailto:${siteConfig.contact.email}`}>
              <span>{siteConfig.contact.email}</span>
            </a>
          </div>
          <div className="footer-company-info">
            {siteConfig.business?.legalName && (
              <p>{siteConfig.business.legalName}</p>
            )}
            {contactAddress && <p>{contactAddress}</p>}
          </div>
        </section>
      </div>

      <div className="footer-wordmark" aria-hidden="true">
        {brandLabel}
      </div>

      <div className="footer-signature">
        <p>{siteConfig.tagline}</p>
      </div>

      <div className="footer-bottom">
        <p>
          © {currentYear} {copyright}
        </p>
        <nav aria-label="Legal">
          {legalLinks.map((item) => (
            <Link key={item.label} to={item.to} onClick={scrollToTop}>
              {item.label}
            </Link>
          ))}
        </nav>
      </div>
    </footer>
  );
}

export default Footer;
